import { eventBus } from '@/engine/events';
import { logger } from '@/engine/logging';
import { map } from 'nanostores';
import { itemsDB } from '@/game/database';

/**
 * Player settings interface - только вкл/выкл
 */
export interface PlayerSettings {
  audio: {
    musicEnabled: boolean;
    sfxEnabled: boolean;
  };
  version: string;
}

/**
 * Inventory item interface
 */
export interface InventoryItem {
  itemId: string;
  quantity: number;
  type: string; // тип предмета из items.ts
  lastUpdated: number;
}

/**
 * Player profile interface - полный профиль игрока
 */
export interface PlayerProfile {
  settings: PlayerSettings;
  gameData: {
    level: number;
    experience: number;
    resources: {
      [itemId: string]: number;
    };
  };
  inventory: {
    items: Record<string, InventoryItem>;
  };
  upgrades: {
    [upgradeId: string]: number; // уровень апгрейда
  };
  stats: {
    totalMined: number;
    totalCoins: number;
    playTime: number;
    lastPlayed: number;
  };
  progression: {
    currentGalaxy: string;
    currentSector: number;
    unlockedSectors: number[];
    completedSectors: number[];
  };
  version: string;
}

/**
 * Default settings values
 */
const DEFAULT_SETTINGS: PlayerSettings = {
  audio: {
    musicEnabled: true,
    sfxEnabled: true,
  },
  version: '1.0.0',
};

/**
 * Default profile values
 */
const DEFAULT_PROFILE: PlayerProfile = {
  settings: DEFAULT_SETTINGS,
  gameData: {
    level: 1,
    experience: 0,
    resources: {},
  },
  inventory: {
    items: {
      credits: {
        itemId: 'credits',
        quantity: 0,
        type: 'currency',
        lastUpdated: Date.now(),
      },
    },
  },
  upgrades: {},
  stats: {
    totalMined: 0,
    totalCoins: 0,
    playTime: 0,
    lastPlayed: Date.now(),
  },
  progression: {
    currentGalaxy: 'G1',
    currentSector: 1,
    unlockedSectors: [1],
    completedSectors: [],
  },
  version: '1.0.0',
};

/**
 * Profile store using Nanostores map
 */
export const profileStore = map<PlayerProfile>(DEFAULT_PROFILE);

/**
 * Settings store - derived from profile for backward compatibility
 */
export const settingsStore = map<PlayerSettings>(DEFAULT_SETTINGS);

/**
 * Profile actions
 */
export const profileActions = {
  /**
   * Update audio settings
   */
  updateAudioSettings(audioConfig: Partial<PlayerSettings['audio']>): void {
    const current = profileStore.get();
    const newAudio = { ...current.settings.audio, ...audioConfig };

    logger.debug('🎵 Updating audio settings:', {
      old: current.settings.audio,
      new: newAudio,
    });

    profileStore.setKey('settings', { ...current.settings, audio: newAudio });
    // Sync with settings store for backward compatibility
    settingsStore.setKey('audio', newAudio);
  },

  /**
   * Update game data
   */
  updateGameData(gameData: Partial<PlayerProfile['gameData']>): void {
    const current = profileStore.get();
    const newGameData = { ...current.gameData, ...gameData };

    console.debug('🎮 Updating game data:', {
      old: current.gameData,
      new: newGameData,
    });

    profileStore.setKey('gameData', newGameData);
  },

  /**
   * Update stats
   */
  updateStats(stats: Partial<PlayerProfile['stats']>): void {
    const current = profileStore.get();
    const newStats = { ...current.stats, ...stats };

    console.debug('📊 Updating stats:', {
      old: current.stats,
      new: newStats,
    });

    profileStore.setKey('stats', newStats);
  },

  /**
   * Update upgrades
   */
  updateUpgrades(upgrades: Partial<PlayerProfile['upgrades']>): void {
    const current = profileStore.get();
    const newUpgrades: Record<string, number> = { ...current.upgrades };

    // Убеждаемся, что все значения - числа
    Object.entries(upgrades).forEach(([key, value]) => {
      if (typeof value === 'number') {
        newUpgrades[key] = value;
      }
    });

    console.debug('⬆️ Updating upgrades:', {
      old: current.upgrades,
      new: newUpgrades,
    });

    profileStore.setKey('upgrades', newUpgrades);
  },

  /**
   * Set upgrade level
   */
  setUpgradeLevel(upgradeId: string, level: number): void {
    const current = profileStore.get();
    const newUpgrades = { ...current.upgrades, [upgradeId]: Math.max(0, level) };

    console.debug('⬆️ Setting upgrade level:', {
      upgradeId,
      level,
      oldLevel: current.upgrades[upgradeId] || 0,
    });

    profileStore.setKey('upgrades', newUpgrades);
  },

  /**
   * Get upgrade level
   */
  getUpgradeLevel(upgradeId: string): number {
    return profileStore.get().upgrades[upgradeId] || 0;
  },

  /**
   * Update resources
   */
  updateResources(resources: Partial<PlayerProfile['gameData']['resources']>): void {
    const current = profileStore.get();
    const newResources: Record<string, number> = { ...current.gameData.resources };

    // Убеждаемся, что все значения - числа
    Object.entries(resources).forEach(([key, value]) => {
      if (typeof value === 'number') {
        newResources[key] = value;
      }
    });

    console.debug('💎 Updating resources:', {
      old: current.gameData.resources,
      new: newResources,
    });

    profileActions.updateGameData({ resources: newResources });
  },

  /**
   * Add resource amount
   */
  addResource(itemId: string, amount: number): void {
    const current = profileStore.get();
    const currentAmount = current.gameData.resources[itemId] || 0;
    profileActions.updateResources({ [itemId]: currentAmount + amount });
  },

  /**
   * Remove resource amount
   */
  removeResource(itemId: string, amount: number): void {
    const current = profileStore.get();
    const currentAmount = current.gameData.resources[itemId] || 0;
    profileActions.updateResources({ [itemId]: Math.max(0, currentAmount - amount) });
  },

  /**
   * Get resource amount
   */
  getResourceAmount(itemId: string): number {
    return profileStore.get().gameData.resources[itemId] || 0;
  },

  // ===== INVENTORY METHODS =====

  /**
   * Add item to inventory
   */
  addInventoryItem(itemId: string, quantity: number = 1): boolean {
    const current = profileStore.get();
    const inventory = current.inventory;
    const existingItem = inventory.items[itemId];

    // Получаем тип предмета из конфига
    const itemType = itemsDB.getItemType(itemId);

    if (existingItem) {
      // Увеличиваем количество существующего предмета
      existingItem.quantity += quantity;
      existingItem.lastUpdated = Date.now();
      inventory.items[itemId] = existingItem;
    } else {
      // Добавляем новый предмет
      const newItem: InventoryItem = {
        itemId,
        quantity,
        type: itemType,
        lastUpdated: Date.now(),
      };
      inventory.items[itemId] = newItem;
    }

    profileStore.setKey('inventory', { ...inventory });
    logger.info('Предмет добавлен в инвентарь', {
      itemId,
      quantity,
      type: itemType,
      source: 'inventory',
    });
    return true;
  },

  /**
   * Remove item from inventory
   */
  removeInventoryItem(itemId: string, quantity: number = 1): boolean {
    const current = profileStore.get();
    const inventory = current.inventory;
    const existingItem = inventory.items[itemId];

    if (!existingItem) {
      logger.warn('Предмет не найден в инвентаре', { itemId, source: 'inventory' });
      return false;
    }

    if (existingItem.quantity <= quantity) {
      // Удаляем предмет полностью
      delete inventory.items[itemId];
    } else {
      // Уменьшаем количество
      existingItem.quantity -= quantity;
      existingItem.lastUpdated = Date.now();
      inventory.items[itemId] = existingItem;
    }

    profileStore.setKey('inventory', { ...inventory });
    logger.info('Предмет удален из инвентаря', { itemId, quantity, source: 'inventory' });
    return true;
  },

  /**
   * Get inventory item quantity
   */
  getInventoryItemQuantity(itemId: string): number {
    const current = profileStore.get();
    const item = current.inventory.items[itemId];
    return item ? item.quantity : 0;
  },

  /**
   * Check if inventory has item
   */
  hasInventoryItem(itemId: string, quantity: number = 1): boolean {
    return this.getInventoryItemQuantity(itemId) >= quantity;
  },

  /**
   * Get all inventory items
   */
  getAllInventoryItems(): InventoryItem[] {
    const current = profileStore.get();
    return Object.values(current.inventory.items);
  },

  /**
   * Clear inventory
   */
  clearInventory(): void {
    const inventory = {
      items: {},
    };

    profileStore.setKey('inventory', inventory);
    logger.info('Инвентарь очищен', { source: 'inventory' });
  },

  /**
   * Get inventory stats
   */
  getInventoryStats() {
    const current = profileStore.get();
    const inventory = current.inventory;
    return {
      itemCount: Object.keys(inventory.items).length,
      totalItems: Object.values(inventory.items).reduce((sum, item) => sum + item.quantity, 0),
    };
  },

  /**
   * Get items by type
   */
  getItemsByType(type: string): InventoryItem[] {
    const current = profileStore.get();
    return Object.values(current.inventory.items).filter(item => item.type === type);
  },

  /**
   * Get total quantity of items by type
   */
  getTotalQuantityByType(type: string): number {
    const items = this.getItemsByType(type);
    return items.reduce((sum, item) => sum + item.quantity, 0);
  },

  // ===== PROGRESSION METHODS =====

  /**
   * Update progression data
   */
  updateProgression(progression: Partial<PlayerProfile['progression']>): void {
    const current = profileStore.get();
    const newProgression = { ...current.progression, ...progression };

    console.debug('🌌 Updating progression:', {
      old: current.progression,
      new: newProgression,
    });

    profileStore.setKey('progression', newProgression);
  },

  /**
   * Set current sector
   */
  setCurrentSector(sectorId: number): boolean {
    const current = profileStore.get();
    if (!current.progression.unlockedSectors.includes(sectorId)) {
      return false; // Sector not unlocked
    }

    this.updateProgression({ currentSector: sectorId });
    return true;
  },

  /**
   * Unlock sector
   */
  unlockSector(sectorId: number): boolean {
    const current = profileStore.get();
    if (current.progression.unlockedSectors.includes(sectorId)) {
      return false; // Already unlocked
    }

    const newUnlockedSectors = [...current.progression.unlockedSectors, sectorId].sort();
    this.updateProgression({ unlockedSectors: newUnlockedSectors });
    return true;
  },

  /**
   * Complete sector
   */
  completeSector(sectorId: number): boolean {
    const current = profileStore.get();
    if (current.progression.completedSectors.includes(sectorId)) {
      return false; // Already completed
    }

    const newCompletedSectors = [...current.progression.completedSectors, sectorId].sort();
    this.updateProgression({ completedSectors: newCompletedSectors });

    // Auto-unlock next sector
    this.unlockSector(sectorId + 1);

    return true;
  },

  /**
   * Get current sector
   */
  getCurrentSector(): number {
    return profileStore.get().progression.currentSector;
  },

  /**
   * Get current galaxy
   */
  getCurrentGalaxy(): string {
    return profileStore.get().progression.currentGalaxy;
  },

  /**
   * Get unlocked sectors
   */
  getUnlockedSectors(): number[] {
    return profileStore.get().progression.unlockedSectors;
  },

  /**
   * Get completed sectors
   */
  getCompletedSectors(): number[] {
    return profileStore.get().progression.completedSectors;
  },

  /**
   * Get coins (currency items)
   */
  getCoins(): number {
    return this.getTotalQuantityByType('currency');
  },

  /**
   * Get ores
   */
  getOres(): InventoryItem[] {
    return this.getItemsByType('ore');
  },

  /**
   * Get bars
   */
  getBars(): InventoryItem[] {
    return this.getItemsByType('bar');
  },

  /**
   * Reset profile to defaults
   */
  resetProfile(): void {
    console.info('🔄 Resetting profile to defaults');

    profileStore.set(DEFAULT_PROFILE);
    settingsStore.set(DEFAULT_SETTINGS);

    // Emit reset event
    eventBus.emit('profile-reset');
  },

  /**
   * Get current profile (read-only)
   */
  getProfile(): Readonly<PlayerProfile> {
    return { ...profileStore.get() };
  },

  /**
   * Load profile from storage
   */
  loadProfile(profile: Partial<PlayerProfile>): void {
    const current = profileStore.get();
    const mergedProfile = { ...DEFAULT_PROFILE, ...current, ...profile };

    profileStore.set(mergedProfile);
    // Sync settings store for backward compatibility
    settingsStore.set(mergedProfile.settings);
  },
};

/**
 * Settings actions - backward compatibility wrapper
 */
export const settingsActions = {
  updateAudioSettings: profileActions.updateAudioSettings,
  resetSettings: profileActions.resetProfile,
  getSettings: () => profileActions.getProfile().settings,
  loadSettings: (settings: Partial<PlayerSettings>) => {
    profileActions.loadProfile({ settings: settings as PlayerSettings });
  },
};

/**
 * Subscribe to profile changes for persistence
 * Используем setTimeout чтобы убедиться, что persistenceService уже инициализирован
 */
setTimeout(() => {
  profileStore.subscribe(profile => {
    // Emit event for persistence service
    eventBus.emit('profile-changed', { profile });
  });
}, 0);

/**
 * Subscribe to settings changes for backward compatibility
 */
settingsStore.subscribe(settings => {
  // Sync settings back to profile
  const current = profileStore.get();
  if (current.settings !== settings) {
    profileStore.setKey('settings', settings);
  }
});

/**
 * Export commonly used selectors
 */
export const profileSelectors = {
  settings: () => profileStore.get().settings,
  gameData: () => profileStore.get().gameData,
  inventory: () => profileStore.get().inventory,
  stats: () => profileStore.get().stats,
  audio: () => profileStore.get().settings.audio,
};

/**
 * Settings selectors - backward compatibility
 */
export const settingsSelectors = {
  audio: () => settingsStore.get().audio,
};
